import React, { useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Loader2, Upload, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * ImageUploader — drag-drop / click to upload multiple images.
 *
 * Props:
 * - endpoint: "product-image" | "order-attachment"
 * - value: string[] (list of storage paths already uploaded)
 * - onChange: (paths: string[]) => void
 * - max: number (default 8)
 * - label: shown above uploader
 */
export default function ImageUploader({ endpoint, value = [], onChange, max = 8, label }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const upload = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const remaining = max - value.length;
    if (remaining <= 0) { toast.error(`Max ${max} images`); return; }
    const toUpload = list.slice(0, remaining);
    setUploading(true);
    const newPaths = [];
    for (const f of toUpload) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name} — not an image`); continue; }
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name} — larger than 8 MB`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post(`/uploads/${endpoint}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        newPaths.push(data.path);
      } catch (e) {
        toast.error(e.response?.data?.detail || `Failed to upload ${f.name}`);
      }
    }
    setUploading(false);
    if (newPaths.length) onChange([...value, ...newPaths]);
  };

  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3">
      {label && <p className="mono-label text-white/60">{label}</p>}
      <div
        data-testid={`uploader-${endpoint}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${dragOver ? "border-blue-500 bg-blue-600/10" : "border-white/15 hover:border-white/40 bg-zinc-950"}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
        />
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-blue-300">
            <Loader2 className="h-4 w-4 animate-spin"/> Uploading…
          </div>
        ) : (
          <div className="text-white/60">
            <Upload className="h-6 w-6 mx-auto mb-2 text-white/40"/>
            <p className="text-sm">Drop images here or click to select</p>
            <p className="text-[10px] font-mono mt-1 text-white/40">JPEG / PNG / WEBP · max 8 MB each · up to {max} images</p>
          </div>
        )}
      </div>

      {value.length > 0 && (
        <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
          {value.map((path, i) => (
            <div key={path} data-testid={`uploaded-${i}`} className="relative group aspect-square border border-white/10 bg-zinc-900 overflow-hidden">
              <img src={`${API}/files/${path}`} alt="" className="w-full h-full object-cover"/>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                className="absolute top-1 right-1 bg-black/80 hover:bg-red-600 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove"
              >
                <X className="h-3 w-3"/>
              </button>
              {i === 0 && (
                <span className="absolute bottom-1 left-1 mono-label text-[9px] bg-blue-600 text-white px-1.5 py-0.5">
                  Main
                </span>
              )}
            </div>
          ))}
          {value.length === 0 && (
            <div className="col-span-full flex items-center justify-center h-16 text-white/40 text-sm">
              <ImageIcon className="h-4 w-4 mr-2"/> No images yet
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Given a product record, return an ordered list of image URLs (main + gallery). */
export function productImageUrls(p) {
  const urls = [];
  if (p?.image_url) urls.push(p.image_url);
  (p?.images || []).forEach((path) => {
    // Support both storage-paths (relative) and full URLs
    if (path.startsWith("http")) urls.push(path);
    else urls.push(`${API}/files/${path}`);
  });
  return urls.length ? urls : [p?.image_url || ""];
}
